import type {
  AnchorHTMLAttributes,
  ComponentPropsWithRef,
  MouseEvent,
  ReactNode,
} from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { cx } from '../cx'
import { Spinner } from '../Loading/Loading'
import { Tooltip } from '../Tooltip/Tooltip'
import styles from './IconButton.module.css'

// `label` becomes `aria-label` and, unless `tooltip` is given, the tooltip.
// `tooltip` is the Tooltip text when it should differ from `label`. Never a
// native `title`.
type Common = {
  label: string
  tooltip?: string
  children: ReactNode
  /** `surface` hovers to raise; `well` (the rail) rests in text-mute. */
  ground?: 'page' | 'surface' | 'well'
}

type AsButton = Omit<
  ComponentPropsWithRef<'button'>,
  'aria-label' | 'title' | 'children'
> &
  Common & { href?: undefined; loading?: boolean }

/** Navigates: a real router link in icon-button paint (D186). */
type AsLink = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'aria-label' | 'title' | 'children' | 'href' | 'target' | 'rel'
> &
  Common & { href: string; state?: unknown }

type IconButtonProps = AsButton | AsLink

export function IconButton(props: IconButtonProps) {
  if (props.href !== undefined) {
    const {
      label,
      tooltip,
      ground = 'page',
      className,
      children,
      href,
      state,
      ...rest
    } = props
    return (
      <Tooltip content={tooltip ?? label}>
        <RouterLink
          {...rest}
          to={href}
          state={state}
          aria-label={label}
          className={cx(styles.iconButton, className)}
          data-ground={ground}
        >
          <span className={styles.glyph} aria-hidden="true">
            {children}
          </span>
        </RouterLink>
      </Tooltip>
    )
  }
  return <IconButtonElement {...props} />
}

function IconButtonElement({
  label,
  tooltip,
  type = 'button',
  ground = 'page',
  loading,
  className,
  onClick,
  children,
  ...rest
}: AsButton) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  return (
    <Tooltip content={tooltip ?? label}>
      <button
        {...rest}
        type={type}
        aria-label={label}
        className={cx(styles.iconButton, className)}
        data-ground={ground}
        aria-busy={loading || undefined}
        aria-disabled={loading || undefined}
        onClick={handleClick}
      >
        <span className={styles.glyph} aria-hidden="true">
          {loading ? <Spinner tone="current" /> : children}
        </span>
      </button>
    </Tooltip>
  )
}
